import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Percent, Target, TrendingDown, Maximize2 } from 'lucide-react';
import { DetailModal } from './DetailModal';
const stats = [{
  label: 'Total P&L',
  value: '+$2,847.32',
  valueColor: 'text-success',
  icon: <TrendingUp size={16} className="text-success" data-id="element-143" />,
  secondaryText: 'vs. prev. period +12.4%',
  clickable: true
}, {
  label: 'ROI',
  value: '+5.67%',
  valueColor: 'text-success',
  icon: <Percent size={16} className="text-brand-purple-light" data-id="element-144" />,
  secondaryText: 'on $50,241 invested',
  clickable: false
}, {
  label: 'Win Rate',
  value: '67.2%',
  valueColor: 'text-text-primary',
  icon: <Target size={16} className="text-brand-purple-light" data-id="element-145" />,
  secondaryText: '98W · 48L · 1.84 avg ratio',
  clickable: true
}, {
  label: 'Max Drawdown',
  value: '-12.3%',
  valueColor: 'text-danger',
  icon: <TrendingDown size={16} className="text-danger" data-id="element-146" />,
  secondaryText: 'Recovery: 89%',
  progress: 28.4,
  clickable: false
}];
export function PnLSummaryCards() {
  const [activeModal, setActiveModal] = useState<string | null>(null);
  return <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-147">
        {stats.map((stat, i) => <motion.div key={stat.label} initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.35,
        delay: i * 0.06
      }} className={`glass-border rounded-[20px] p-4 flex flex-col justify-between ${stat.clickable ? 'cursor-pointer hover:bg-white/[0.02] transition-colors' : ''}`} onClick={stat.clickable ? () => setActiveModal(stat.label) : undefined} data-id="element-148">
            <div data-id="element-149">
              <div className="flex items-center justify-between mb-2" data-id="element-150">
                <span className="text-[10px] font-label text-text-tertiary uppercase" data-id="element-151">
                  {stat.label}
                </span>
                <div className="flex items-center gap-1" data-id="element-152">
                  {stat.clickable && <Maximize2 size={10} className="text-text-tertiary" data-id="element-153" />}
                  <div className="p-1.5 bg-background-elevated rounded-lg" data-id="element-154">
                    {stat.icon}
                  </div>
                </div>
              </div>
              <div className="flex items-baseline gap-2 mb-2" data-id="element-155">
                <span className={`text-xl font-numbers font-light ${stat.valueColor}`} data-id="element-156">
                  {stat.value}
                </span>
              </div>
              {stat.progress !== undefined && <div className="w-full h-1 bg-background-elevated rounded-full mb-2 overflow-hidden" data-id="element-157">
                  <div className="h-full bg-danger rounded-full" style={{
              width: `${stat.progress}%`
            }} data-id="element-158" />
                </div>}
            </div>
            <div className="text-[10px] text-text-tertiary" data-id="element-159">
              {stat.secondaryText}
            </div>
          </motion.div>)}
      </div>

      {/* Total P&L Modal */}
      <DetailModal isOpen={activeModal === 'Total P&L'} onClose={() => setActiveModal(null)} title="P&L Breakdown — All Trades" data-id="element-160">
        <div className="space-y-2" data-id="element-161">
          {[{
          pair: 'BTC/USDT',
          side: 'Buy',
          pnl: '+$412.50',
          date: 'Apr 28',
          size: '$2,450'
        }, {
          pair: 'ETH/USDT',
          side: 'Sell',
          pnl: '+$287.30',
          date: 'Apr 27',
          size: '$1,800'
        }, {
          pair: 'SOL/USDT',
          side: 'Buy',
          pnl: '+$198.40',
          date: 'Apr 26',
          size: '$950'
        }, {
          pair: 'BTC/USDT',
          side: 'Buy',
          pnl: '-$198.20',
          date: 'Apr 25',
          size: '$3,200'
        }, {
          pair: 'ETH/USDT',
          side: 'Sell',
          pnl: '+$156.80',
          date: 'Apr 24',
          size: '$1,400'
        }, {
          pair: 'BNB/USDT',
          side: 'Buy',
          pnl: '-$89.50',
          date: 'Apr 23',
          size: '$620'
        }, {
          pair: 'SOL/USDT',
          side: 'Sell',
          pnl: '+$134.20',
          date: 'Apr 22',
          size: '$780'
        }, {
          pair: 'BTC/USDT',
          side: 'Buy',
          pnl: '+$245.60',
          date: 'Apr 21',
          size: '$2,100'
        }].map((trade, i) => <div key={i} className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-white/[0.02] transition-colors" data-id="element-162">
              <div className="flex items-center gap-3" data-id="element-163">
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${trade.side === 'Buy' ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`} data-id="element-164">
                  {trade.side}
                </span>
                <span className="text-sm text-text-primary font-medium" data-id="element-165">
                  {trade.pair}
                </span>
                <span className="text-xs text-text-tertiary" data-id="element-166">{trade.size}</span>
              </div>
              <div className="flex items-center gap-3" data-id="element-167">
                <span className="text-xs text-text-tertiary" data-id="element-168">{trade.date}</span>
                <span className={`text-sm font-numbers ${trade.pnl.startsWith('+') ? 'text-success' : 'text-danger'}`} data-id="element-169">
                  {trade.pnl}
                </span>
              </div>
            </div>)}
        </div>
      </DetailModal>

      {/* Win Rate Modal */}
      <DetailModal isOpen={activeModal === 'Win Rate'} onClose={() => setActiveModal(null)} title="Win/Loss Breakdown" data-id="element-170">
        <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-171">
          <div className="bg-success/5 border border-success/10 rounded-xl p-4 text-center" data-id="element-172">
            <div className="text-2xl font-numbers text-success mb-1" data-id="element-173">98</div>
            <div className="text-[10px] font-label text-text-tertiary uppercase" data-id="element-174">
              Winning Trades
            </div>
          </div>
          <div className="bg-danger/5 border border-danger/10 rounded-xl p-4 text-center" data-id="element-175">
            <div className="text-2xl font-numbers text-danger mb-1" data-id="element-176">48</div>
            <div className="text-[10px] font-label text-text-tertiary uppercase" data-id="element-177">
              Losing Trades
            </div>
          </div>
        </div>
        <div className="space-y-2" data-id="element-178">
          <div className="text-[10px] font-label text-text-tertiary uppercase mb-2" data-id="element-179">
            Recent Outcomes
          </div>
          {[{
          pair: 'BTC/USDT',
          result: 'Win',
          pnl: '+$412.50',
          date: 'Apr 28'
        }, {
          pair: 'ETH/USDT',
          result: 'Win',
          pnl: '+$287.30',
          date: 'Apr 27'
        }, {
          pair: 'SOL/USDT',
          result: 'Win',
          pnl: '+$198.40',
          date: 'Apr 26'
        }, {
          pair: 'BTC/USDT',
          result: 'Loss',
          pnl: '-$198.20',
          date: 'Apr 25'
        }, {
          pair: 'ETH/USDT',
          result: 'Win',
          pnl: '+$156.80',
          date: 'Apr 24'
        }, {
          pair: 'BNB/USDT',
          result: 'Loss',
          pnl: '-$89.50',
          date: 'Apr 23'
        }].map((trade, i) => <div key={i} className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-white/[0.02] transition-colors" data-id="element-180">
              <div className="flex items-center gap-3" data-id="element-181">
                <div className={`w-1.5 h-1.5 rounded-full ${trade.result === 'Win' ? 'bg-success' : 'bg-danger'}`} data-id="element-182" />
                <span className="text-sm text-text-primary font-medium" data-id="element-183">
                  {trade.pair}
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-184">
                <span className="text-xs text-text-tertiary" data-id="element-185">{trade.date}</span>
                <span className={`text-sm font-numbers ${trade.pnl.startsWith('+') ? 'text-success' : 'text-danger'}`} data-id="element-186">
                  {trade.pnl}
                </span>
              </div>
            </div>)}
        </div>
      </DetailModal>
    </>;
}